<script lang="ts" setup>
    import type { Ref } from 'vue';
    import type { SelectOption } from '~/components/shared/SelectInput.vue';

    type ValidationField = {
        readonly $error: boolean;
        readonly $errors: Array<{
            readonly $message: string | Ref<string>;
        }>;
    };

    const props = defineProps<{
        countryId?: string;
        stateId?: string;
        countryIdValidation?: ValidationField;
        stateIdValidation?: ValidationField;
    }>();

    const emit = defineEmits<(e: 'update:countryId' | 'update:stateId', value: string) => void>();

    const { t } = useI18n();

    const GERMANY_ISO = ['DE'];

    const { countryId, stateId } = useVModels(props, emit);
    const { getCountries, getStatesForCountry } = useCountries();
    const states = computed(() => {
        return getStatesForCountry(countryId.value || '');
    });
    function onCountrySelectChanged() {
        stateId.value = '';
    }

    const countryOptions = computed<SelectOption[]>(() => [
        {
            label: t('form.chooseCountry'),
            value: '',
            selected: true,
            disabled: true
        },
        ...getCountries.value
            .filter((option) => option.iso && GERMANY_ISO.includes(option.iso))
            .map((option) => ({
                label: option.name,
                value: option.id,
                selected: false
            }))
    ]);

    const stateOptions = computed<SelectOption[]>(() => [
        {
            label: t('form.chooseState'),
            value: '',
            selected: true,
            disabled: true
        },
        ...(states.value?.map((option) => ({
            label: option.name,
            value: option.id,
            selected: false
        })) || [])
    ]);
</script>

<template>
    <div class="shared-input-wrapper">
        <div>
            <SelectInput
                v-model="countryId"
                :options="countryOptions"
                :label="`${t('form.country')}`"
                :required="true"
                class="full-width"
                data-testid="checkout-pi-country-input"
                @change="onCountrySelectChanged"
            />

            <span
                v-if="countryIdValidation?.$error"
                class="select-error"
            >
                {{ countryIdValidation?.$errors[0]?.$message }}
            </span>
        </div>
        <div v-if="states && states.length">
            <SelectInput
                id="state"
                v-model="stateId"
                :options="stateOptions"
                :label="`${t('form.state')}`"
                autocomplete="off"
                class="full-width"
                data-testid="checkout-pi-state-input"
            />

            <span
                v-if="stateIdValidation?.$error"
                class="select-error"
            >
                {{ stateIdValidation?.$errors[0]?.$message }}
            </span>
        </div>
    </div>
</template>

<style scoped>
    .shared-input-wrapper {
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }
</style>
